import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout'; // Reuse tokens
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function GitHubChangeControlPage() {
  const faqs = [
    {
      question: 'Does EvidentTrail change repository settings?',
      answer: 'No. EvidentTrail uses read-only GitHub App access and does not modify code or settings.'
    },
    {
      question: 'Is this a compliance certification?',
      answer: 'No. EvidentTrail generates evidence packs to support audit reviews; it does not certify compliance.'
    },
    {
      question: 'What data is collected?',
      answer: 'Metadata for pull requests, approvals, branch protection settings, and CI check status. No source code or build logs.'
    },
    {
      question: 'Can I run a one-off report?',
      answer: 'Yes. Guest mode supports one report for one repository with short retention.'
    }
  ];

  const longformSections = [
    {
      title: 'What auditors ask for',
      paragraphs: [
        'Auditors typically request evidence that approvals happened before merges, required checks were satisfied, and branch protections were enforced during the audit window.'
      ],
      bullets: [
        'Approval evidence tied to each change',
        'Required check status and timestamps',
        'Branch protection settings in effect'
      ]
    },
    {
      title: 'Evidence artifacts included',
      paragraphs: [
        'Evidence packs include PR approvals, CI check status metadata, branch protection snapshots, and exception records when present.'
      ],
      bullets: [
        'PR approvals + reviewer identities',
        'Required checks status metadata',
        'Branch protection settings and rulesets',
        'Exception records for bypasses'
      ]
    },
    {
      title: 'How EvidentTrail works (read-only)',
      paragraphs: [
        'EvidentTrail connects to GitHub using read-only permissions and captures metadata needed for change control evidence. No code or settings are modified.'
      ],
      bullets: [
        'Read-only GitHub App access',
        'Metadata-only evidence capture',
        'Exportable packs with SHA-256 checksums'
      ]
    },
    {
      title: 'How to interpret the output',
      paragraphs: [
        'Use approval timestamps to confirm review timing and the check status table to show tests were required and passed.'
      ],
      bullets: [
        'Approvals occur before merge',
        'Required checks are marked as passed',
        'Checksums validate export integrity'
      ]
    },
    {
      title: 'Common failure modes',
      paragraphs: [
        'Teams often rely on screenshots or missing approval evidence, which leads to audit findings. Evidence packs prevent gaps in review history.'
      ],
      bullets: [
        'Approvals recorded after merge',
        'Branch protection changes without documentation',
        'Evidence exports without timestamps'
      ]
    }
  ];

  return (
    <MarketingLayout
      title="GitHub Change Control for SOC 2 & ISO 27001 | EvidentTrail"
      description="Automate change control evidence collection from GitHub. Prove peer reviews, CI checks, and approvals to support SOC 2 and ISO 27001 readiness without manual screenshots."
      h1="GitHub Change Management Evidence"
      heroText="Turn your GitHub pull request workflow into a documented change control record."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'GitHub Change Control', url: 'https://evidenttrail.com/github-change-control' }
        ]}
      />
      <FaqJsonLd items={faqs} />
      {/* Problem Statement */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>The Audit Challenge</h2>
              <p className={`${tokens.text.base} mb-6`}>
                Auditors need proof that every production change was authorized, reviewed, and tested.
                Engineering teams often struggle to provide this because:
              </p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                <span className={tokens.text.base}>
                  <strong className="text-white">Screenshots don't scale:</strong> Capturing PRs and approval screens manually is time-consuming and prone to error.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                <span className={tokens.text.base}>
                  <strong className="text-white">Sample-based audits miss gaps:</strong> Auditors only check 5-10% of changes, leaving risk hidden in the other 90%.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                <span className={tokens.text.base}>
                  <strong className="text-white">Context is lost:</strong> Linking work item identifiers to code merges manually is difficult.
                </span>
              </li>
            </ul>
            <p className={`${tokens.text.base} mt-6`}>
              For audit-grade traceability, connect PRs to approved{' '}
              <Link to="/work-item-linking" className="text-primary-400 hover:underline">work items</Link>
              so auditors can verify business justification.
            </p>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      {/* Control Objectives and Traced Change */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What Auditors Commonly Review</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Auditors review change control evidence to validate that your organization meets Trust Services Criteria like CC8.1 (change management controls). Specifically, they expect evidence showing:
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>For Each Sampled Change</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Request:</strong> Link to Jira/ADO <Link to="/work-item-linking" className="text-primary-400 hover:underline">work item</Link> with business justification
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Review:</strong> Pull request with peer review comments and approval
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Testing:</strong> CI check results (unit tests, linting)
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Deployment:</strong> Timestamp showing when change was merged to production
                    </span>
                  </li>
                </ul>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Control Objectives Validated</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">CC8.1:</strong> Changes are authorized before implementation
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">CC6.6:</strong> Logical access controls prevent unauthorized changes
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">CC7.1:</strong> Changes are tested prior to deployment
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">CC7.2:</strong> Detection of anomalous conditions (drift, bypass)
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <h3 className={`${tokens.heading.h3} mb-4`}>Example: Traced Change</h3>
            <div className={`${tokens.card} mb-4`}>
              <div className="font-mono text-sm space-y-1 text-slate-300">
                <div>• <strong className="text-white">Work Item:</strong> PROJ-1234 "Add rate limiting to API endpoint"</div>
                <div>• <strong className="text-white">PR:</strong> #567 opened by alice@company.com on 2024-01-15</div>
                <div>• <strong className="text-white">Reviewer:</strong> bob@company.com approved on 2024-01-16</div>
                <div>• <strong className="text-white">CI Checks:</strong> All 15 checks passed (unit tests, lint)</div>
                <div>• <strong className="text-white">Branch Protection:</strong> Enforce at time of merge (verified)</div>
                <div>• <strong className="text-white">Merged:</strong> 2024-01-16 14:23 UTC to main</div>
                <div>• <strong className="text-white">Deployed:</strong> 2024-01-16 14:45 UTC (auto-deploy triggered)</div>
              </div>
            </div>
            <p className={`${tokens.text.small} text-slate-400 italic`}>
              This complete chain demonstrates traceability from business need → code change → review → testing → production.
            </p>
          </div>
        </div>
      </section>

      {/* Continuous vs Point-in-Time */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Why Continuous Evidence Matters</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Continuous evidence allows auditors to assess whether controls operate consistently throughout the audit period, rather than only at the time of audit. This distinction is critical for SOC 2 Type II audits, which test controls over the audit period.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="border border-red-500/30 bg-red-950/20 rounded-lg p-6">
                <h3 className={`${tokens.heading.h3} mb-3 text-red-300`}>Point-in-Time (Risky)</h3>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li>❌ Evidence collected retroactively during audit</li>
                  <li>❌ Requires manual screenshots and exports</li>
                  <li>❌ Cannot prove controls operated all year</li>
                  <li>❌ High risk of missing or incomplete data</li>
                  <li>❌ 8-16 hours of manual work per audit</li>
                </ul>
              </div>
              <div className="border border-green-500/30 bg-green-950/20 rounded-lg p-6">
                <h3 className={`${tokens.heading.h3} mb-3 text-green-300`}>Continuous (Recommended)</h3>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li>✅ Evidence captured as events occur (contemporaneous)</li>
                  <li>✅ Automated collection with no manual effort</li>
                  <li>✅ Demonstrates consistent control operation</li>
                  <li>✅ Complete record of all changes, not just samples</li>
                  <li>✅ Instant audit package generation</li>
                </ul>
              </div>
            </div>
            <p className={`${tokens.text.base} mb-4`}>
              Organizations using continuous evidence typically achieve <strong className="text-white">50-70% reduction in audit preparation time</strong> and significantly lower risk of audit findings related to missing or inconsistent evidence.
            </p>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Automated Evidence Collection</h2>
            <p className={`${tokens.text.base} mb-6`}>
              EvidentTrail connects to your GitHub organization with <strong>read-only access</strong> and automatically logs every change event.
            </p>
            <div className={`${tokens.card} mb-8`}>
              <h3 className={`${tokens.heading.h3} mb-4`}>What We Capture</h3>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-white font-medium mb-2">Pull Requests</h4>
                  <ul className="text-sm text-slate-400 space-y-2">
                    <li>• Author & Description</li>
                    <li>• Target Branch</li>
                    <li>• Linked Work Items (Tickets)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-2">Reviews & Approvals</h4>
                  <ul className="text-sm text-slate-400 space-y-2">
                    <li>• Reviewer Identity</li>
                    <li>• Approval Timestamps</li>
                    <li>• Branch Protection Rules</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-2">Validation</h4>
                  <ul className="text-sm text-slate-400 space-y-2">
                    <li>• CI/CD Status Checks</li>
                    <li>• Merge Commit SHAs</li>
                    <li>• Deployment Timestamps</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-2">Security</h4>
                  <ul className="text-sm text-slate-400 space-y-2">
                    <li>• CI check status</li>
                    <li>• AI-Assisted Flagging</li>
                    <li>• Bypass Justifications</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="text-center">
              <a href={appLinks.register} className="btn btn-primary px-8 py-3">
                Connect GitHub & Start Tracking
              </a>
              <p className="text-sm text-slate-500 mt-4">Read-only permissions with evidence generated from GitHub metadata.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Linking / Hub */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Compliance Topics</h2>
          <div className="grid md:grid-cols-5 gap-6">
            <Link to="/soc2-change-management-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">SOC 2 Evidence Maps</h4>
              <p className="text-sm text-slate-400">How our evidence maps specifically to CC8.1 and CC6.1 criteria.</p>
            </Link>
            <Link to="/read-only-github-audit" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Read-Only Audits</h4>
              <p className="text-sm text-slate-400">Technical details on our secure, non-intrusive connection method.</p>
            </Link>
            <Link to="/tools/pr-approvals-report" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">PR Approvals Report Tool</h4>
              <p className="text-sm text-slate-400">Generate a one-time report of your repository's approval evidence.</p>
            </Link>
            <Link to="/solutions" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Solutions</h4>
              <p className="text-sm text-slate-400">Explore compliance-focused evidence workflows.</p>
            </Link>
            <Link to="/security" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Security</h4>
              <p className="text-sm text-slate-400">Read-only access and data minimization boundaries.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
